<template>
  <div class="company-leads-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="leads-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">
                <span class="page-icon">👥</span>
                Company Leads
              </h1>
              <p class="page-description">Manage required lead information for your company</p>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading lead types...</p>
          </div>

          <!-- Main Content -->
          <div v-else class="leads-content">
            <div class="leads-grid-container">

              <!-- Available to Add Section -->
              <div class="leads-section available-section">
                <h3 class="section-title">
                  <span class="section-icon">📋</span>
                  Available to Add
                  <span class="count-badge">{{ unassignedLeads.length }}</span>
                </h3>
                <p class="section-description">
                  Click to add these fields as requirements for lead collection.
                </p>

                <div v-if="unassignedLeads.length === 0" class="empty-section">
                  <div class="empty-icon">✅</div>
                  <p>All lead types are currently required</p>
                </div>

                <div v-else class="leads-list">
                  <div
                    v-for="lead in unassignedLeads"
                    :key="'available-' + lead.id"
                    class="lead-card available-card"
                    :class="{ 'processing': processingLeads.includes(lead.id) }"
                  >
                  <div class="lead-info">
                    <div class="lead-icon">{{ getLeadIcon(lead.name) }}</div>
                    <div class="lead-details">
                      <h4 class="lead-name">{{ lead.name }}</h4>
                      <p class="lead-description">{{ getLeadDescription(lead.name) }}</p>
                    </div>
                  </div>

                  <div class="lead-actions">
                    <button
                      v-if="!isLeadAssigned(lead.id)"
                      @click="assignLead(lead.id)"
                      :disabled="processingLeads.includes(lead.id)"
                      class="btn-assign"
                    >
                      <span v-if="processingLeads.includes(lead.id)" class="loading-spinner-small"></span>
                      <span v-else class="action-icon">+</span>
                      {{ processingLeads.includes(lead.id) ? 'Adding...' : 'Add' }}
                    </button>

                    <button
                      v-else
                      @click="removeLead(lead.id)"
                      :disabled="processingLeads.includes(lead.id)"
                      class="btn-remove"
                    >
                      <span v-if="processingLeads.includes(lead.id)" class="loading-spinner-small"></span>
                      <span v-else class="action-icon">-</span>
                      {{ processingLeads.includes(lead.id) ? 'Removing...' : 'Remove' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Currently Required Section -->
            <div class="leads-section required-section">
              <h3 class="section-title">
                <span class="section-icon">✅</span>
                Currently Required
                <span class="count-badge">{{ assignedLeads.length }}</span>
              </h3>
              <p class="section-description">
                Click to remove these fields from your lead collection requirements.
              </p>

              <div v-if="assignedLeads.length === 0" class="empty-section">
                <div class="empty-icon">📝</div>
                <p>No lead requirements set yet</p>
              </div>

              <div v-else class="leads-list">
                <div
                  v-for="lead in assignedLeadObjects"
                  :key="'assigned-' + lead.id"
                  class="lead-card required-card"
                  :class="{ 'processing': processingLeads.includes(lead.id) }"
                >
                  <div class="lead-info">
                    <div class="lead-icon">{{ getLeadIcon(lead.name) }}</div>
                    <div class="lead-details">
                      <h4 class="lead-name">{{ lead.name }}</h4>
                      <p class="lead-description">{{ getLeadDescription(lead.name) }}</p>
                    </div>
                  </div>

                  <div class="lead-actions">
                    <button
                      @click="removeLead(lead.id)"
                      :disabled="processingLeads.includes(lead.id)"
                      class="btn-remove"
                    >
                      <span v-if="processingLeads.includes(lead.id)" class="loading-spinner-small"></span>
                      <span v-else class="action-icon">-</span>
                      {{ processingLeads.includes(lead.id) ? 'Removing...' : 'Remove' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </main>
    </div>

    <!-- Success Snackbar -->
    <div v-if="showSuccessNotification" class="snackbar success-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">✅</span>
        <span class="snackbar-message">{{ successMessage }}</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

    <!-- Error Snackbar -->
    <div v-if="showErrorNotification" class="snackbar error-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">❌</span>
        <span class="snackbar-message">{{ errorMessage }}</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { DashboardService } from '../services/dashboard'

export default {
  name: 'CompanyLeadsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: true,
      availableLeads: [],
      assignedLeads: [], // Array of lead IDs
      assignedLeadObjects: [], // Array of full lead objects for assigned leads
      processingLeads: [], // Track which leads are being processed
      showSuccessNotification: false,
      showErrorNotification: false,
      successMessage: '',
      errorMessage: '',
      notificationTimeout: null
    }
  },
  computed: {
    unassignedLeads () {
      return this.availableLeads.filter(lead => !this.assignedLeads.includes(lead.id))
    }
  },
  async mounted () {
    await this.loadData()
  },
  methods: {
    async loadData () {
      try {
        this.loading = true

        // Load both available leads and company's assigned leads
        const [availableResult, companyResult] = await Promise.all([
          DashboardService.getAvailableLeads(),
          DashboardService.getCompanyLeads()
        ])

        if (availableResult.success) {
          this.availableLeads = availableResult.leads
        } else {
          console.error('Failed to load available leads:', availableResult.error)
        }

        if (companyResult.success) {
          // Store both the lead objects and IDs for assigned leads
          this.assignedLeadObjects = companyResult.companyLeads
          this.assignedLeads = companyResult.companyLeads.map(cl => cl.id)
        } else {
          console.error('Failed to load company leads:', companyResult.error)
        }
      } catch (error) {
        console.error('Error loading lead data:', error)
        this.displayErrorNotification('Failed to load lead data')
      } finally {
        this.loading = false
      }
    },

    async assignLead (leadId) {
      if (this.processingLeads.includes(leadId)) return

      try {
        this.processingLeads.push(leadId)

        const result = await DashboardService.assignLeadToCompany(leadId)

        if (result.success) {
          this.assignedLeads.push(leadId)
          // Also add the full lead object to assignedLeadObjects
          const leadObj = this.availableLeads.find(l => l.id === leadId)
          if (leadObj) {
            this.assignedLeadObjects.push(leadObj)
          }
          this.displaySuccessNotification(`${this.getLeadNameById(leadId)} added to requirements`)
        } else {
          console.error('Failed to assign lead:', result.error)
          this.displayErrorNotification(result.message || 'Failed to add lead requirement')
        }
      } catch (error) {
        console.error('Error assigning lead:', error)
        this.displayErrorNotification('An error occurred while adding the lead requirement')
      } finally {
        this.processingLeads = this.processingLeads.filter(id => id !== leadId)
      }
    },

    async removeLead (leadId) {
      if (this.processingLeads.includes(leadId)) return

      try {
        this.processingLeads.push(leadId)

        const result = await DashboardService.removeLeadFromCompany(leadId)

        if (result.success) {
          this.assignedLeads = this.assignedLeads.filter(id => id !== leadId)
          // Also remove from assignedLeadObjects
          this.assignedLeadObjects = this.assignedLeadObjects.filter(lead => lead.id !== leadId)
          this.displaySuccessNotification(`${this.getLeadNameById(leadId)} removed from requirements`)
        } else {
          console.error('Failed to remove lead:', result.error)
          this.displayErrorNotification(result.message || 'Failed to remove lead requirement')
        }
      } catch (error) {
        console.error('Error removing lead:', error)
        this.displayErrorNotification('An error occurred while removing the lead requirement')
      } finally {
        this.processingLeads = this.processingLeads.filter(id => id !== leadId)
      }
    },

    getLeadNameById (leadId) {
      // First check in assigned leads
      let lead = this.assignedLeadObjects.find(l => l.id === leadId)
      // If not found, check in available leads
      if (!lead) {
        lead = this.availableLeads.find(l => l.id === leadId)
      }
      return lead ? lead.name : 'Unknown'
    },

    getLeadIcon (leadName) {
      const iconMap = {
        Name: '👤',
        'Email Address': '📧',
        'Phone Number': '📞',
        'Company Name': '🏢',
        'Job Title': '💼',
        Age: '🎂',
        Gender: '⚧️',
        'Location or Address': '📍',
        'Business Activity': '🏭',
        'National ID / Passport ID': '🆔'
      }
      return iconMap[leadName] || '📋'
    },

    getLeadDescription (leadName) {
      const descriptionMap = {
        Name: 'Full name of the contact person',
        'Email Address': 'Primary email address for communication',
        'Phone Number': 'Contact phone number',
        'Company Name': 'Name of the company or organization',
        'Job Title': 'Position or role within the company',
        Age: 'Age of the contact person',
        Gender: 'Gender identification',
        'Location or Address': 'Physical address or location',
        'Business Activity': 'Type of business or industry',
        'National ID / Passport ID': 'Government identification number'
      }
      return descriptionMap[leadName] || 'Lead information field'
    },

    isLeadAssigned (leadId) {
      return this.assignedLeads.includes(leadId)
    },

    displaySuccessNotification (message) {
      this.hideNotification()
      this.successMessage = message
      this.showSuccessNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 4000)
    },

    displayErrorNotification (message) {
      this.hideNotification()
      this.errorMessage = message
      this.showErrorNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 5000)
    },

    hideNotification () {
      this.showSuccessNotification = false
      this.showErrorNotification = false
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout)
        this.notificationTimeout = null
      }
    }
  },

  beforeUnmount () {
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout)
    }
  }
}
</script>

<style scoped>
.company-leads-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.leads-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  text-align: center;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
  transition: color 0.3s ease;
}

.page-icon {
  font-size: 32px;
}

.page-description {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

/* Sections */
.leads-section {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
  transition: color 0.3s ease;
}

.section-icon {
  font-size: 22px;
  opacity: 0.8;
}

.section-description {
  color: var(--text-secondary);
  margin: 0 0 24px;
  line-height: 1.5;
  transition: color 0.3s ease;
}

/* Leads Grid Container */
.leads-grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.leads-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.available-section {
  border-left: 4px solid #3b82f6;
}

.required-section {
  border-left: 4px solid #10b981;
}

.count-badge {
  background: var(--text-secondary);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
  transition: background-color 0.3s ease;
}

.available-section .count-badge {
  background: #3b82f6;
}

.required-section .count-badge {
  background: #10b981;
}

.lead-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  background: var(--bg-primary);
  transition: all 0.2s;
  cursor: pointer;
}

.lead-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
  transform: translateY(-2px);
}

.available-card {
  border-color: var(--border-primary);
  background: var(--bg-secondary);
}

.available-card:hover {
  border-color: #3b82f6;
  background: var(--bg-tertiary);
}

.required-card {
  border-color: #10b981;
  background: var(--bg-secondary);
}

.required-card:hover {
  border-color: #059669;
  background: var(--bg-tertiary);
}

.lead-card.processing {
  opacity: 0.7;
  pointer-events: none;
}

.lead-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.lead-icon {
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border-radius: 10px;
  transition: background-color 0.3s ease;
}

.required-card .lead-icon {
  background: rgba(16, 185, 129, 0.1);
}

.available-card .lead-icon {
  background: rgba(59, 130, 246, 0.1);
}

.lead-details {
  flex: 1;
  min-width: 0;
}

.lead-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px;
  transition: color 0.3s ease;
}

.lead-description {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.3;
  transition: color 0.3s ease;
}

/* Action Buttons */
.lead-actions {
  flex-shrink: 0;
}

.btn-assign,
.btn-remove {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
  justify-content: center;
}

.btn-assign {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-assign:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-remove {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

.btn-remove:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
}

.btn-assign:disabled,
.btn-remove:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.action-icon {
  font-size: 12px;
}

/* Empty Section States */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px;
  color: var(--text-secondary);
  text-align: center;
  background: var(--bg-tertiary);
  border-radius: 8px;
  border: 2px dashed var(--border-secondary);
  transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
}

.empty-section .empty-icon {
  font-size: 32px;
  opacity: 0.5;
}

.empty-section p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  transition: border-color 0.3s ease;
}

.loading-spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

/* Snackbar Notifications */
.snackbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 3000;
  min-width: 300px;
  max-width: 500px;
  animation: slideUp 0.3s ease-out;
  backdrop-filter: blur(10px);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.success-snackbar {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.error-snackbar {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.snackbar-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.snackbar-message {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.snackbar-close {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
}

.snackbar-close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.1);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 64px;
  }

  .page-content {
    padding: 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .leads-grid-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .available-card,
  .required-card {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .lead-info {
    justify-content: flex-start;
  }

  .lead-actions {
    align-self: stretch;
  }

  .btn-assign,
  .btn-remove {
    width: 100%;
  }

  .empty-section {
    padding: 24px 16px;
  }

  .snackbar {
    left: 16px;
    right: 16px;
    transform: none;
    min-width: auto;
    max-width: none;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>
